/**
 * @file category-form-utils.ts
 *
 * Pure helpers for the category form: deriving editable fields from a category,
 * dirty comparison, and building the {@link ShopCategoryPayload} sent on save.
 * Keeping these pure makes the mapping logic unit-testable without rendering. A
 * blank slug is left out of the payload so the engine derives one from the name.
 */

import type { ShopCategory, ShopCategoryPayload } from '../../api/shop-types';
import type { CategoryFormFields } from './category-form-types';

/**
 * Derives the editable form fields from a source category.
 *
 * @param category - The source category, or `undefined` for create mode.
 *
 * @returns The initial {@link CategoryFormFields}.
 */
export function deriveFields(category?: ShopCategory): CategoryFormFields {
  return {
    name: category?.name ?? '',
    slug: category?.slug ?? '',
    parent: category?.parent ?? '',
    description: category?.description ?? '',
    imageSrc: category?.image?.src ?? '',
    imageId: category?.image?.id ?? null,
  };
}

/**
 * Shallow-compares two field sets for dirty tracking.
 *
 * @param a - One field set.
 * @param b - The other field set.
 *
 * @returns `true` when every field is strictly equal.
 */
export function shallowEqual(a: CategoryFormFields, b: CategoryFormFields): boolean {
  const keys = Object.keys(a) as (keyof CategoryFormFields)[];
  return keys.every((key) => a[key] === b[key]);
}

/**
 * Builds the write payload from the form's fields. A blank slug/description is
 * omitted; the image is sent as `{ id, src }` (attached), `{ src }` (new upload),
 * or `null` (none / cleared).
 *
 * @param fields - The current form fields.
 *
 * @returns The {@link ShopCategoryPayload} to POST/PUT.
 */
export function buildCategoryPayload(fields: CategoryFormFields): ShopCategoryPayload {
  const payload: ShopCategoryPayload = {
    name: fields.name.trim(),
    parent: fields.parent,
  };
  const slug = fields.slug.trim();
  if (slug) {
    payload.slug = slug;
  }
  const description = fields.description.trim();
  if (description) {
    payload.description = description;
  }
  payload.image = fields.imageSrc
    ? fields.imageId != null
      ? { id: fields.imageId, src: fields.imageSrc }
      : { src: fields.imageSrc }
    : null;
  return payload;
}
